import { Collapse, Grid, TableCell, Tooltip } from '@material-ui/core';
import { ThemeProvider } from '@material-ui/styles';
import { isValueEmpty } from '@topconagriculture/frontend-utils';
import classNames from 'classnames';
import MUIDataTable from 'mui-datatables';
import * as React from 'react';
import { Waypoint } from 'react-waypoint';
import { compose, defaultProps, withHandlers, withProps } from 'recompose';
import { Loader } from '@topconagriculture/loader';
import { Checkbox } from '../Form/Checkbox';
import { Panel } from '../Panel';
import { withBaseProps } from '../Utils';
import styles, { tableOverrideTheme } from './DataTableStyles';
import DataTableToolbar from './DataTableToolbar';
import { useFilteredColumns } from './useFilteredColumns';
const GROUP_KEY = 'default';
const ACTIONS_KEY = 'actions';
const SELECTION_KEY = 'selection';
const DataTable = (props) => {
    const { actionColumnDef, classes, className, defaultFilterTitle = '', emptyComponent, filterGroups = [], FooterComponent, isOpen, selectable, selectColumnDef, selected, setColumnConfig, setIsOpen, setSelectedItems, showActions = true, showCollapseButton, showColumnsButton = true, storedColumns, tableColumns, tableData, tableId, tableOptions, title = '', toolbarComponent, resetState = true, customIcons, allButTitle, testId = 'DataTable', } = props;
    const [filteredColumns, filters, filterStateChangeHandler, tableColumnsUpdateHandler] = useFilteredColumns(tableColumns, storedColumns, setColumnConfig);
    React.useEffect(() => {
        if (resetState) {
            tableColumnsUpdateHandler();
        }
    }, [tableColumns, resetState]);
    if (showActions) {
        filteredColumns.push(actionColumnDef);
    }
    if (selectable) {
        filteredColumns.unshift(selectColumnDef);
    }
    const collapsed = showCollapseButton && !isOpen;
    const table = emptyComponent && !tableData.length ? (emptyComponent) : (React.createElement(MUIDataTable, { "data-testid": `${testId}-table`, title: '', options: tableOptions, columns: filteredColumns, data: tableData }));
    return (React.createElement(React.Fragment, null,
        React.createElement(Panel, { "data-testid": `${testId}`, className: classNames(classes.root, className, { [classes.rootCollapsed]: collapsed }), classes: {
                content: classes.panelContent,
                header: classNames({ [classes.headerCollapsed]: collapsed }),
            }, header: toolbarComponent || (React.createElement(DataTableToolbar, { "data-testid": `${testId}-toolbar`, columns: tableColumns, defaultFilterTitle: defaultFilterTitle, defaultGroup: GROUP_KEY, filterGroups: filterGroups, filterState: filters, isOpen: isOpen, onFilterChange: filterStateChangeHandler, setIsOpen: setIsOpen, showCollapseButton: showCollapseButton, showColumnsButton: showColumnsButton, title: title, customIcons: customIcons })) },
            React.createElement(ThemeProvider, { theme: tableOverrideTheme }, showCollapseButton ? (React.createElement(Collapse, { in: isOpen, mountOnEnter: true, unmountOnExit: true }, table)) : (table))),
        FooterComponent && (React.createElement("div", { "data-testid": `${testId}-footer`, className: classes.tableFooter },
            React.createElement(FooterComponent, { selected: selected, tableId: tableId, setSelectedItems: setSelectedItems })))));
};
export default compose(defaultProps({
    selectionMapper: (item, index) => ({ value: index, label: '' }),
}), withBaseProps({ styles }), withHandlers({
    rowRenderer: ({ pageInfo, data = [], showActions = true, classes, loadMore, pageInfoHasNextKey = 'hasNextPage', pageInfoEndCursorKey = 'endCursor', }) => (rowData, dataIndex, rowIndex) => {
        const isLastRow = rowIndex === data.length - 1;
        const endCursor = String(pageInfo && pageInfo[pageInfoEndCursorKey]);
        const hasNextPage = pageInfo && pageInfo[pageInfoHasNextKey];
        return (React.createElement(React.Fragment, { key: rowIndex },
            React.createElement("tr", null, rowData.map((d, i) => {
                const isActionCell = showActions && i === rowData.length - 1;
                return (React.createElement(TableCell, { key: `${rowIndex}-${dataIndex}-${i}`, className: classNames({ [classes.tableCellAction]: isActionCell }) }, d));
            })),
            loadMore && isLastRow && hasNextPage ? (React.createElement(Waypoint, { key: endCursor, onEnter: loadMore },
                React.createElement("tr", null,
                    React.createElement(TableCell, { colSpan: rowData.length },
                        React.createElement(Grid, { container: true, justifyContent: "flex-start" },
                            React.createElement(Loader, { size: 30, thickness: 4, disableShrink: true })))))) : null));
    },
    tableHeadRenderer: ({ classes }) => (label = ' ') => (meta) => (React.createElement(TableCell, { key: meta.index, className: classNames(classes.tableCell, classes.tableHeaderCell) }, label)),
    tableCellRenderer: ({ emptyValue = '' }) => (value) => (!isValueEmpty(value) ? value : emptyValue),
    actionColumnRenderer: ({ classes }) => (meta) => (React.createElement(TableCell, { key: meta.index, className: classNames(classes.tableCell, classes.tableHeaderActionCell) })),
    selectColumnRenderer: ({ classes, data, selectionMapper = (item, index) => ({ value: `${index}`, label: '' }), setSelectedItems, selected, allBut, selectedAll, setSelectedAll, allButTitle, }) => (meta) => {
        const checked = selected.length === data.length;
        const defaultItem = allBut ? !selectedAll && !checked : checked;
        const selectItems = defaultItem
            ? []
            : data.map((dataItem, index) => selectionMapper(dataItem, index));
        const checkbox = (React.createElement(Checkbox, { className: classes.checkbox, indeterminate: allBut && selectedAll && !!selected.length, onChange: () => {
                if (allBut) {
                    const nextValue = !selectedAll;
                    setSelectedAll(nextValue);
                    if (!nextValue) {
                        setSelectedItems([]);
                    }
                    else {
                        setSelectedItems(selectItems);
                    }
                }
                else {
                    setSelectedItems(selectItems);
                }
            }, checked: allBut ? selectedAll : checked }));
        return (React.createElement(TableCell, { key: meta.index, className: classNames(classes.tableCell, classes.tableHeaderCell) }, allButTitle ? (React.createElement(Tooltip, { title: allButTitle, placement: "top" }, checkbox)) : (checkbox)));
    },
}), withProps(({ actionColumn, actionColumnRenderer, ActionComponent, actionComponentProps, columns = [], data, options, rowRenderer, showActions = true, tableCellRenderer, tableHeadRenderer, selectable, selectColumn, selectColumnRenderer, selected, selectionMapper = (item, index) => ({ value: `${index}`, label: '' }), setSelectedItems, allBut, selectedAll, }) => ({
    tableData: data.map((dataItem, dataIndex) => {
        const selectionItem = selectionMapper(dataItem, dataIndex);
        const existingIndex = selected.map((selectedItem) => selectedItem.value).indexOf(selectionItem.value);
        const checked = existingIndex > -1;
        const newSelectedItems = checked
            ? [...selected.slice(0, existingIndex), ...selected.slice(existingIndex + 1, selected.length)]
            : [...selected, selectionItem];
        return {
            ...dataItem,
            ...(showActions &&
                ActionComponent && {
                [ACTIONS_KEY]: React.createElement(ActionComponent, Object.assign({}, dataItem, actionComponentProps)),
            }),
            ...(selectable && {
                [SELECTION_KEY]: (React.createElement(Checkbox, { checked: allBut && selectedAll ? !checked : checked, onChange: () => setSelectedItems(newSelectedItems) })),
            }),
        };
    }),
    tableColumns: columns.map((column) => ({
        defaultRemoveCheckedValue: true,
        group: GROUP_KEY,
        removable: true,
        removeDisabled: false,
        ...column,
        name: column.name || '',
        options: {
            customHeadRender: tableHeadRenderer(column.label),
            customBodyRender: tableCellRenderer,
            options: {
                filter: false,
                sort: false,
            },
            ...column.options,
        },
        removeFieldLabel: column.removeFieldLabel || column.label,
    })),
    tableOptions: {
        customRowRender: rowRenderer,
        download: false,
        elevation: 0,
        filter: false,
        pagination: false,
        print: false,
        responsive: 'scrollMaxHeight',
        rowHover: false,
        search: false,
        selectableRows: 'none',
        sort: false,
        viewColumns: false,
        ...options,
    },
    actionColumnDef: {
        isFixedRight: true,
        rowDataKey: ACTIONS_KEY,
        name: ACTIONS_KEY,
        options: {
            customHeadRender: actionColumnRenderer,
        },
        ...actionColumn,
    },
    selectColumnDef: {
        isFixedRight: false,
        rowDataKey: SELECTION_KEY,
        name: SELECTION_KEY,
        options: {
            customHeadRender: selectColumnRenderer,
        },
        ...selectColumn,
    },
})))(DataTable);
//# sourceMappingURL=DataTable.js.map